import type { AuditEntryDto } from '@agent-platform/shared';
import { useEffect, useState } from 'react';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/format';
import { usePaged } from '@/lib/queries';

/** Keeps only characters the API accepts, so typing never produces a 400. */
const clean = (value: string, pattern: RegExp) => value.toLowerCase().replace(pattern, '');

/** Debounces by value: callers pass a fresh object every render, which must not restart the timer. */
function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  const key = JSON.stringify(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(JSON.parse(key) as T), ms);
    return () => clearTimeout(timer);
  }, [key, ms]);
  return debounced;
}

function AuditTable({ tenantId }: { tenantId: string }) {
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const filters = useDebounced({ action, targetType });
  const params = new URLSearchParams();
  if (filters.action) params.set('action', filters.action);
  if (filters.targetType) params.set('targetType', filters.targetType);
  const query = params.toString();
  const log = usePaged<AuditEntryDto>(
    ['audit', tenantId, query],
    `/audit-log${query ? `?${query}` : ''}`,
  );
  const rows = log.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="audit-action" className="text-muted-foreground text-xs">
              Action starts with
            </Label>
            <Input
              id="audit-action"
              className="w-56"
              placeholder="e.g. agent. or member."
              value={action}
              maxLength={100}
              onChange={(e) => setAction(clean(e.target.value, /[^a-z0-9_.]/g))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="audit-target" className="text-muted-foreground text-xs">
              Target type
            </Label>
            <Input
              id="audit-target"
              className="w-44"
              placeholder="e.g. agent"
              value={targetType}
              maxLength={64}
              onChange={(e) => setTargetType(clean(e.target.value, /[^a-z0-9_]/g))}
            />
          </div>
        </div>
        <ErrorAlert error={log.error} />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.id} data-testid="audit-row">
                <TableCell className="whitespace-nowrap">{formatDateTime(e.createdAt)}</TableCell>
                <TableCell>{e.actorEmail ?? 'system'}</TableCell>
                <TableCell className="font-mono text-xs">{e.action}</TableCell>
                <TableCell className="text-xs">{e.targetType}</TableCell>
                <TableCell className="max-w-96 font-mono text-xs">
                  {e.diff ? (
                    <details>
                      <summary className="cursor-pointer truncate">
                        {JSON.stringify(e.diff)}
                      </summary>
                      <pre className="bg-muted mt-1 overflow-auto rounded p-2 whitespace-pre-wrap">
                        {JSON.stringify(e.diff, null, 2)}
                      </pre>
                    </details>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!log.isPending && rows.length === 0 && (
          <p className="text-muted-foreground py-6 text-center text-sm">No matching entries.</p>
        )}
        {log.hasNextPage && (
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => log.fetchNextPage()}
            disabled={log.isFetchingNextPage}
          >
            Load more
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function AuditLogPage() {
  return (
    <>
      <PageHeader title="Audit log" description="Every administrative change in this tenant." />
      <RequireTenant>{(tenant) => <AuditTable tenantId={tenant.id} />}</RequireTenant>
    </>
  );
}
